import React, { createContext, useContext, useState, ReactNode, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

import { PsToastDialog } from 'stencilSetup';

interface Toast {
  id: string;
  message: string;
  status: 'info' | 'warning' | 'success' | 'danger';
  closeIcon: boolean;
  extraClass: string;
  showBackdrop: boolean;
  dialogStatic: boolean;
  container: HTMLElement | null;
  show: boolean;
  targetClass?: string;
}

interface MessageQueue {
  message: string;
  status: 'info' | 'warning' | 'success' | 'danger';
  timestamp: number;
}

export interface ToastOptions {
  message: string;
  status?: 'info' | 'warning' | 'success' | 'danger';
  closeIcon?: boolean;
  extraClass?: string;
  showBackdrop?: boolean;
  dialogStatic?: boolean;
  container?: HTMLElement;
  targetClass?: string;
}

interface ToastDialogContextType {
  showToast: (options: ToastOptions) => void;
  hideToast: (id: string) => void;
}

const ToastDialogContext = createContext<ToastDialogContextType | undefined>(undefined);

const noop = () => {
  /* intentionally empty */
};

const mockToastContext: ToastDialogContextType = {
  showToast: noop,
  hideToast: noop,
};

export const useToastDialog = () => {
  const context = useContext(ToastDialogContext);
  if (context === undefined) {
    // Return mock context only when explicitly requested
    if (process.env.NODE_ENV === 'test' && process.env.TEST_MOCK_TOAST === 'true') {
      return mockToastContext;
    }
    throw new Error('useToastDialog must be used within a ToastDialogProvider');
  }
  return context;
};

export const ToastDialogProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const toastRefs = useRef<Map<string, HTMLPsToastDialogElement>>(new Map());
  const observerRefs = useRef<Map<string, MutationObserver>>(new Map());
  const nextIdRef = useRef(1);
  const messageQueueRef = useRef<MessageQueue[]>([]);
  const MESSAGE_DEBOUNCE_TIME = 2000; // 2 seconds

  const isDuplicateMessage = (message: string, status: 'info' | 'warning' | 'success' | 'danger'): boolean => {
    const now = Date.now();
    // Clean up old messages
    messageQueueRef.current = messageQueueRef.current.filter(
      msg => now - msg.timestamp <= MESSAGE_DEBOUNCE_TIME
    );

    // Check for duplicate
    const isDuplicate = messageQueueRef.current.some(
      msg => msg.message === message && msg.status === status
    );

    if (!isDuplicate) {
      messageQueueRef.current.push({ message, status, timestamp: now });
    }

    return isDuplicate;
  };

  const showToast = (options: ToastOptions) => {
    if (isDuplicateMessage(options.message, options.status || 'info')) {
      return;
    }

    const id = `toast_${nextIdRef.current++}`;
    const newToast: Toast = {
      id,
      message: options.message,
      status: options.status || 'info',
      closeIcon: options.closeIcon || false,
      extraClass: options.extraClass || '',
      showBackdrop: options.showBackdrop || false,
      dialogStatic: options.dialogStatic || false,
      container: options.container || null,
      targetClass: options.targetClass,
      show: false,
    };
    
    setToasts(prev => [...prev, newToast]);

    // Create observer for this toast
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          const dialog = document.getElementById(`parent_${id}`)?.querySelector('dialog');
          if (dialog) {
            observer.disconnect();
            setToasts(current => 
              current.map(t => 
                t.id === id ? { ...t, show: true } : t
              )
            );
          }
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });

    observerRefs.current.set(id, observer);
  };

  const hideToast = (id: string) => {
    const toastRef = toastRefs.current.get(id);
    if (toastRef) {
      toastRef.setAttribute('open', 'false');
      setToasts(prev => prev.filter(t => t.id !== id));
      toastRefs.current.delete(id);
      
      const observer = observerRefs.current.get(id);
      if (observer) {
        observer.disconnect();
        observerRefs.current.delete(id);
      }
    }
  };

  const handleClose = (id: string) => {
    hideToast(id);
  };

  // Cleanup on unmount
  useEffect(() => {
    const observers = observerRefs.current;
    return () => {
      observers.forEach(observer => observer.disconnect());
    };
  }, []);

  return (
    <ToastDialogContext.Provider value={{ showToast, hideToast }}>
      {children}
      {toasts.map(toast => {
        const Toast = (
          <PsToastDialog
            key={toast.id}
            dialogId={toast.id}
            show={toast.show}
            status={toast.status}
            closeIcon={toast.closeIcon}
            showBackdrop={toast.showBackdrop}
            dialogStatic={toast.dialogStatic}
            targetClass={toast.targetClass}
            closingMethod={() => handleClose(toast.id)}
            ref={ref => {
              if (ref) {
                toastRefs.current.set(toast.id, ref);
              }
            }}
          >
            {toast.message}
          </PsToastDialog>
        );

        return toast.container
          ? createPortal(Toast, toast.container)
          : Toast;
      })}
    </ToastDialogContext.Provider>
  );
};
